const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtu.be',
  'www.youtu.be',
]);

const YOUTUBE_VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

function sanitizeVideoId(candidate: string | null | undefined): string | null {
  if (!candidate) return null;
  return YOUTUBE_VIDEO_ID_PATTERN.test(candidate) ? candidate : null;
}

export function isYouTubeUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return YOUTUBE_HOSTS.has(parsed.hostname);
  } catch {
    return false;
  }
}

export function extractYouTubeVideoId(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (!YOUTUBE_HOSTS.has(parsed.hostname)) return null;

    if (parsed.hostname === 'youtu.be' || parsed.hostname === 'www.youtu.be') {
      return sanitizeVideoId(parsed.pathname.slice(1).split('/')[0]);
    }

    const vParam = parsed.searchParams.get('v');
    if (vParam) return sanitizeVideoId(vParam);

    const pathSegments = parsed.pathname.split('/').filter(Boolean);
    if (pathSegments.length >= 2) {
      const prefix = pathSegments[0];
      if (prefix === 'embed' || prefix === 'v' || prefix === 'shorts') {
        return sanitizeVideoId(pathSegments[1]);
      }
    }

    return null;
  } catch {
    return null;
  }
}

export function toYouTubeEmbedUrl(url: string): string | null {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) return null;
  return `https://www.youtube.com/embed/${videoId}`;
}

export function buildYouTubeEmbedSrc(
  url: string,
  options: {
    autoplay?: boolean;
    loop?: boolean;
    muted?: boolean;
    controls?: boolean;
  } = {},
): string | null {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) return null;

  const params = new URLSearchParams();
  if (options.autoplay) {
    params.set('autoplay', '1');
    params.set('mute', '1');
  }
  if (options.loop) {
    params.set('loop', '1');
    params.set('playlist', videoId);
  }
  if (options.muted && !options.autoplay) params.set('mute', '1');
  if (options.controls === false) params.set('controls', '0');

  const qs = params.toString();
  return `https://www.youtube.com/embed/${videoId}${qs ? `?${qs}` : ''}`;
}

export function getYouTubeThumbnailUrl(url: string): string | null {
  const videoId = extractYouTubeVideoId(url);
  if (!videoId) return null;
  return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
}
